import React, { useState } from 'react';

const OUTREACH_STAGES = [
  {
    key: 'intro',
    icon: '👋',
    label: 'First Contact (legacy)',
    template: 'wizard_follow_up',
    color: '#3b82f6',
    trigger: 'LEGACY: pre-report-first visitors who started the wizard but hadn\'t completed analysis',
    description: 'A short personal hello — you saw they started, everything is saved, and you\'re happy to help. Edit the draft to mention something about their business.',
    sends: 'Editable personal note + continue link + your signature',
  },
  {
    key: 'report',
    icon: '📊',
    label: 'Analysis Report',
    template: 'wizard_report_follow_up',
    color: '#0f766e',
    trigger: 'Visitor completed AI analysis but didn\'t proceed to file',
    description: 'Their trademark report, delivered personally — risk level, conflicts, classes, recommendation, with an offer to walk them through it.',
    sends: 'Editable personal note + full analysis report card (toggleable) + continue link + your signature',
  },
  {
    key: 'quote_report',
    icon: '📄',
    label: 'Report & Quote',
    template: 'quote_report',
    color: '#0f766e',
    trigger: 'Visitor requested their free report — sent from the Reports section after review (24h SLA)',
    description: 'The formal report: live IP Australia register findings, coverage rationale and their confirmed quote, with a dashboard sign-in link (no password needed). Competing outreach is suppressed while a report is in the queue.',
    sends: 'Editable personal note + full report & quote card + fresh dashboard magic link + your signature',
  },
  {
    key: 'quote_nudge',
    icon: '💳',
    label: 'First Nudge',
    template: 'quote_follow_up',
    color: '#d97706',
    trigger: 'Report sent but no payment after a few days',
    description: 'A gentle reminder that their report and confirmed quote are waiting in their dashboard, with the first-to-file rationale and an offer to resend the sign-in link.',
    sends: 'Editable personal note + dashboard link + your signature',
  },
  {
    key: 'checkout',
    icon: '🛒',
    label: 'Checkout Nudge (legacy)',
    template: 'wizard_checkout_follow_up',
    color: '#d97706',
    trigger: 'LEGACY: pre-quote-first visitors who reached countries/details/checkout but didn\'t pay. Not recommended for new-flow visitors.',
    description: 'A friendly "you were almost done" — their selections are saved, attorney review reassurance, and an invitation to ask what held them back.',
    sends: 'Editable personal note + their filing summary (toggleable) + continue link + your signature',
  },
  {
    key: 'second',
    icon: '⏰',
    label: 'Second Nudge (final note)',
    template: 'wizard_second_follow_up',
    color: '#8b5cf6',
    trigger: 'Already nudged once — this is the last email in the sequence',
    description: 'A respectful sign-off: "this will be my last note". Mentions first-to-file and any flagged conflicts, without pressure tactics.',
    sends: 'Editable personal note + first-to-file context + continue link + your signature',
  },
];

function OutreachStageLegend() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mkt-outreach-legend">
      <div className="mkt-outreach-legend-header" onClick={() => setExpanded(!expanded)}>
        <h3 className="mkt-outreach-legend-title">
          Outreach Stages
          <span className="mkt-outreach-legend-count">{OUTREACH_STAGES.length} templates</span>
        </h3>
        <button className="mkt-outreach-legend-toggle">
          {expanded ? '▾ Hide' : '▸ Show Guide'}
        </button>
      </div>

      {/* Compact inline badges always visible */}
      <div className="mkt-outreach-legend-badges">
        {OUTREACH_STAGES.map((stage) => (
          <span
            key={stage.key}
            className="mkt-outreach-legend-badge"
            style={{ background: stage.color + '12', color: stage.color, borderColor: stage.color + '30' }}
            title={stage.trigger}
          >
            {stage.icon} {stage.label}
          </span>
        ))}
        <span className="mkt-outreach-legend-arrow">→</span>
        <span
          className="mkt-outreach-legend-badge"
          style={{ background: '#0ea5e912', color: '#0ea5e9', borderColor: '#0ea5e930' }}
          title="No email — worked by phone via the printable call sheet"
        >
          📞 Call list
        </span>
        <span
          className="mkt-outreach-legend-badge"
          style={{ background: '#64748b12', color: '#64748b', borderColor: '#64748b30' }}
          title="Feedback asked — waiting on their answer, no further nudges"
        >
          💬 Feedback
        </span>
        <span className="mkt-outreach-legend-arrow">→</span>
        <span className="mkt-outreach-legend-badge" style={{ background: '#22c55e15', color: '#16a34a', borderColor: '#22c55e30' }}>
          ✅ Paid
        </span>
      </div>

      {/* Expanded detail view */}
      {expanded && (
        <div className="mkt-outreach-legend-detail">
          {OUTREACH_STAGES.map((stage) => (
            <div key={stage.key} className="mkt-outreach-legend-item" style={{ borderLeftColor: stage.color }}>
              <div className="mkt-outreach-legend-item-header">
                <span className="mkt-outreach-legend-item-icon">{stage.icon}</span>
                <div>
                  <strong className="mkt-outreach-legend-item-label">{stage.label}</strong>
                  <code className="mkt-outreach-legend-item-template">{stage.template}</code>
                </div>
              </div>
              <div className="mkt-outreach-legend-item-trigger">
                <strong>When:</strong> {stage.trigger}
              </div>
              <div className="mkt-outreach-legend-item-desc">{stage.description}</div>
              <div className="mkt-outreach-legend-item-sends">
                <strong>Includes:</strong> {stage.sends}
              </div>
            </div>
          ))}
          <div className="mkt-outreach-legend-note">
            Emails go out <strong>signed as you</strong> — the sending address is shown in the composer, and replies land in that inbox.
            Every template is just a starting draft: edit anything before sending.
            All emails include a <strong>continue token</strong> — clicking the CTA auto-restores the visitor's session to their exact wizard step. No login required.
          </div>
        </div>
      )}
    </div>
  );
}

export default OutreachStageLegend;
